import { useState, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { gql } from '@apollo/client';
import {
  Alert,
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid2,
  IconButton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Speed as SpeedIcon,
} from '@mui/icons-material';

import PageHeader from '../../components/PageHeader';
import CrawlerSuccessChart from './CrawlerSuccessChart';
import DomainReputationTable from './DomainReputationTable';
import ErrorMetricsPanel from './ErrorMetricsPanel';

// GraphQL Queries
const GET_HYBRID_SCRAPER_STATUS = gql`
  query getHybridScraperStatus {
    crawlerHybridScraperEnabled
  }
`;

const GET_CRAWLER_OVERVIEW = gql`
  query getCrawlerOverview($startDate: String, $endDate: String) {
    crawlerOverview(startDate: $startDate, endDate: $endDate) {
      totalRequests
      successCount
      failureCount
      successRate
      avgResponseTimeMs
      totalCrawls
      executedCrawls
      skippedCrawls
      trend {
        changePct
        direction
      }
      timestamp
    }
  }
`;

const GET_CRAWLER_TIMELINE = gql`
  query getCrawlerTimeline($startDate: String!, $endDate: String!, $interval: String) {
    crawlerTimeline(startDate: $startDate, endDate: $endDate, interval: $interval) {
      timestamp
      totalCount
      executedCount
      skippedCount
      successRate
    }
  }
`;

const GET_CRAWLER_DOMAIN_LEADERBOARD = gql`
  query getCrawlerDomainLeaderboard(
    $startDate: String!
    $endDate: String!
    $sortBy: String
    $order: String
    $limit: Int
  ) {
    crawlerDomainLeaderboard(startDate: $startDate, endDate: $endDate, sortBy: $sortBy, order: $order, limit: $limit) {
      domain
      crawlCount
      executedCount
      lastCrawl
      reputationScore
      reputationCategory
      successCount
      failureCount
      totalRequests
      successRate
      avgResponseTimeMs
      isAllowlisted
      isBlocklisted
    }
  }
`;

const GET_CRAWLER_ERROR_BREAKDOWN = gql`
  query getCrawlerErrorBreakdown($startDate: String!, $endDate: String!) {
    crawlerErrorBreakdown(startDate: $startDate, endDate: $endDate) {
      totalFailures
      categories {
        blocked
        poorReputation
        fairReputation
      }
      errorRate
    }
  }
`;

const GET_CRAWLER_ALERTS = gql`
  query getCrawlerAlerts($startDate: String, $endDate: String) {
    crawlerAlerts(startDate: $startDate, endDate: $endDate) {
      severity
      type
      message
      currentValue
      threshold
    }
  }
`;

type TimeRange = '24h' | '7d' | '30d';

const CrawlerDashboard = () => {
  const theme = useTheme();
  const [timeRange, setTimeRange] = useState<TimeRange>('24h');
  const [autoRefresh, setAutoRefresh] = useState(true);

  // Calculate date range based on selected time range
  const { startDate, endDate, interval } = useMemo(() => {
    const end = new Date();
    const start = new Date();

    switch (timeRange) {
      case '24h':
        start.setHours(start.getHours() - 24);
        return { startDate: start.toISOString(), endDate: end.toISOString(), interval: 'hourly' };
      case '7d':
        start.setDate(start.getDate() - 7);
        return { startDate: start.toISOString(), endDate: end.toISOString(), interval: 'daily' };
      case '30d':
        start.setDate(start.getDate() - 30);
        return { startDate: start.toISOString(), endDate: end.toISOString(), interval: 'daily' };
      default:
        return { startDate: start.toISOString(), endDate: end.toISOString(), interval: 'hourly' };
    }
  }, [timeRange]);

  // Query hooks
  const { data: hybridScraperData } = useQuery(GET_HYBRID_SCRAPER_STATUS);

  const {
    loading: overviewLoading,
    error: overviewError,
    data: overviewData,
    refetch: refetchOverview,
  } = useQuery(GET_CRAWLER_OVERVIEW, {
    variables: { startDate, endDate },
    pollInterval: autoRefresh ? 60000 : 0, // 1 minute
    fetchPolicy: 'cache-and-network',
  });

  const {
    loading: timelineLoading,
    error: timelineError,
    data: timelineData,
    refetch: refetchTimeline,
  } = useQuery(GET_CRAWLER_TIMELINE, {
    variables: { startDate, endDate, interval },
    pollInterval: autoRefresh ? 60000 : 0,
    fetchPolicy: 'cache-and-network',
  });

  const {
    loading: leaderboardLoading,
    error: leaderboardError,
    data: leaderboardData,
    refetch: refetchLeaderboard,
  } = useQuery(GET_CRAWLER_DOMAIN_LEADERBOARD, {
    variables: { startDate, endDate, sortBy: 'success_rate', order: 'desc', limit: 20 },
    pollInterval: autoRefresh ? 60000 : 0,
    fetchPolicy: 'cache-and-network',
  });

  const {
    loading: errorBreakdownLoading,
    error: errorBreakdownError,
    data: errorBreakdownData,
    refetch: refetchErrorBreakdown,
  } = useQuery(GET_CRAWLER_ERROR_BREAKDOWN, {
    variables: { startDate, endDate },
    pollInterval: autoRefresh ? 60000 : 0,
    fetchPolicy: 'cache-and-network',
  });

  const {
    loading: alertsLoading,
    error: alertsError,
    data: alertsData,
  } = useQuery(GET_CRAWLER_ALERTS, {
    variables: { startDate, endDate },
    pollInterval: autoRefresh ? 30000 : 0, // 30 seconds for alerts
    fetchPolicy: 'cache-and-network',
  });

  const handleRefreshAll = () => {
    refetchOverview();
    refetchTimeline();
    refetchLeaderboard();
    refetchErrorBreakdown();
  };

  const overview = overviewData?.crawlerOverview;
  const alerts = alertsData?.crawlerAlerts || [];

  return (
    <Box>
      <PageHeader
        title="Crawler Dashboard"
        subtitle="Monitor crawler success rates, domain reputation, and bot protection metrics"
        variant="default"
      />

      <Box sx={{ p: 3 }}>
        {/* Controls */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <ToggleButtonGroup
            value={timeRange}
            exclusive
            onChange={(_, value) => value && setTimeRange(value)}
            size="small"
          >
            <ToggleButton value="24h">24 Hours</ToggleButton>
            <ToggleButton value="7d">7 Days</ToggleButton>
            <ToggleButton value="30d">30 Days</ToggleButton>
          </ToggleButtonGroup>

          <Box display="flex" gap={1}>
            <Tooltip title={autoRefresh ? 'Auto-refresh enabled' : 'Auto-refresh disabled'}>
              <IconButton
                size="small"
                onClick={() => setAutoRefresh(!autoRefresh)}
                sx={{
                  color: autoRefresh ? 'primary.main' : 'text.secondary',
                }}
              >
                <ScheduleIcon />
              </IconButton>
            </Tooltip>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefreshAll} size="small">
              Refresh
            </Button>
          </Box>
        </Box>

        {/* Hybrid Scraper Status Check */}
        {!hybridScraperData?.crawlerHybridScraperEnabled && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2" fontWeight={600}>
              Domain Reputation Tracking Disabled
            </Typography>
            <Typography variant="caption" color="text.secondary">
              The hybrid scraper (USE_HYBRID_SCRAPER) is not enabled. Domain reputation metrics will show zero until the
              feature flag is enabled in Doppler and containers are restarted.
            </Typography>
          </Alert>
        )}

        {/* Alerts */}
        {alerts.length > 0 && (
          <Stack spacing={1} mb={3}>
            {alerts.map((alert: any, index: number) => (
              <Alert key={index} severity={alert.severity === 'CRITICAL' ? 'error' : 'warning'} icon={<ErrorIcon />}>
                <Typography variant="body2" fontWeight={600}>
                  {alert.message}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Current: {alert.currentValue?.toFixed(1)}% | Threshold: {alert.threshold?.toFixed(1)}%
                </Typography>
              </Alert>
            ))}
          </Stack>
        )}

        {/* Overview Metrics */}
        <Grid2 container spacing={2} mb={3}>
          {/* Success Rate */}
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <Card
              sx={{
                height: '100%',
                minHeight: 160,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.9)} 0%, ${alpha(theme.palette.primary.dark, 0.95)} 100%)`,
                color: '#FFFFFF',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              }}
            >
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <CheckCircleIcon sx={{ fontSize: 20 }} />
                  <Typography variant="subtitle2" fontWeight={600}>
                    Success Rate
                  </Typography>
                </Box>
                <Typography variant="h3" fontWeight={700}>
                  {overview?.successRate?.toFixed(1) ?? '0'}%
                </Typography>
                {overview?.trend && (
                  <Box display="flex" alignItems="center" gap={0.5} mt={1}>
                    {overview.trend.direction === 'up' ? (
                      <TrendingUpIcon fontSize="small" />
                    ) : (
                      <TrendingDownIcon fontSize="small" />
                    )}
                    <Typography variant="caption">
                      {overview.trend.changePct?.toFixed(1)}% vs previous period
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid2>

          {/* Total Requests */}
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ height: '100%', minHeight: 160 }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Total Requests
                </Typography>
                <Typography variant="h4" fontWeight={700} color="primary.main">
                  {overview?.totalRequests?.toLocaleString() ?? '0'}
                </Typography>
                <Box display="flex" gap={1} mt={1}>
                  <Chip
                    label={`Success: ${overview?.successCount?.toLocaleString() ?? '0'}`}
                    size="small"
                    color="success"
                  />
                  <Chip
                    label={`Failed: ${overview?.failureCount?.toLocaleString() ?? '0'}`}
                    size="small"
                    color="error"
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid2>

          {/* Avg Response Time */}
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ height: '100%', minHeight: 160 }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <SpeedIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Avg Response Time
                  </Typography>
                </Box>
                <Typography variant="h4" fontWeight={700} color="primary.main">
                  {overview?.avgResponseTimeMs ?? '0'}ms
                </Typography>
              </CardContent>
            </Card>
          </Grid2>

          {/* Total Crawls */}
          <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
            <Card sx={{ height: '100%', minHeight: 160 }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Crawl Status
                </Typography>
                <Typography variant="h4" fontWeight={700} color="primary.main">
                  {overview?.totalCrawls?.toLocaleString() ?? '0'}
                </Typography>
                <Box display="flex" gap={1} mt={1}>
                  <Chip label={`Executed: ${overview?.executedCrawls?.toLocaleString() ?? '0'}`} size="small" />
                  <Chip
                    label={`Skipped: ${overview?.skippedCrawls?.toLocaleString() ?? '0'}`}
                    size="small"
                    variant="outlined"
                  />
                </Box>
              </CardContent>
            </Card>
          </Grid2>
        </Grid2>

        {/* Charts Section */}
        <Grid2 container spacing={3} mb={3}>
          {/* Timeline Chart */}
          <Grid2 size={{ xs: 12, lg: 8 }}>
            <CrawlerSuccessChart
              data={timelineData?.crawlerTimeline || []}
              loading={timelineLoading}
              error={timelineError}
            />
          </Grid2>

          {/* Error Breakdown */}
          <Grid2 size={{ xs: 12, lg: 4 }}>
            <ErrorMetricsPanel
              data={errorBreakdownData?.crawlerErrorBreakdown}
              loading={errorBreakdownLoading}
              error={errorBreakdownError}
            />
          </Grid2>
        </Grid2>

        {/* Domain Leaderboard */}
        <DomainReputationTable
          data={leaderboardData?.crawlerDomainLeaderboard || []}
          loading={leaderboardLoading}
          error={leaderboardError}
          onRefresh={refetchLeaderboard}
        />
      </Box>
    </Box>
  );
};

export default CrawlerDashboard;
